import { useState } from 'react';
import { usePhoneInput, defaultCountries, parseCountry } from 'react-international-phone';
import { ChevronsUpDown, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';

function getFlagEmoji(iso2: string): string {
  return iso2
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  defaultCountry?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  onBlur?: () => void;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /** "floating" renders a borderless inline layout for use inside a FloatingInput-style wrapper */
  variant?: 'default' | 'floating';
}

const countries = defaultCountries.map((c) => parseCountry(c));

function PhoneInput({
  value,
  onChange,
  id,
  name,
  defaultCountry = 'cm',
  error,
  disabled,
  required,
  onBlur,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  placeholder,
  className,
  inputClassName,
  variant = 'default',
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);

  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry,
    value,
    countries: defaultCountries,
    disableDialCodeAndPrefix: true,
    onChange: (data) => {
      onChange(data.phone);
    },
  });

  const countryPopover = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === 'floating' ? (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={`Sélectionner le pays, ${country.name} +${country.dialCode}`}
            disabled={disabled}
            className="inline-flex shrink-0 items-center gap-1.5 border-r border-neutral-200 pr-3 mr-3 text-sm transition-colors hover:text-charcoal-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-lg leading-none">{getFlagEmoji(country.iso2)}</span>
            <span className="font-medium text-charcoal-500">+{country.dialCode}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-40" />
          </button>
        ) : (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={`Sélectionner le pays, ${country.name} +${country.dialCode}`}
            disabled={disabled}
            className={cn(
              'inline-flex h-12 shrink-0 items-center gap-1 rounded-l-[var(--store-radius-control,theme(borderRadius.md))] border border-r-0 border-[var(--store-border,theme(colors.slate.200))] bg-[var(--store-surface,white)] text-[var(--store-ink,theme(colors.charcoal.950))] px-2.5 text-sm transition-all focus-visible:outline-none focus-visible:border-[var(--store-accent)] focus-visible:ring-2 focus-visible:ring-[var(--store-accent)]/30 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-rose-600 focus-visible:border-rose-600',
            )}
          >
            <span className="text-lg leading-none">{getFlagEmoji(country.iso2)}</span>
            <span className="text-xs text-[var(--store-ink-muted,theme(colors.slate.500))]">
              +{country.dialCode}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] border border-[var(--store-border,theme(colors.slate.200))] bg-[var(--store-surface,white)] text-[var(--store-ink,theme(colors.charcoal.950))] p-0 shadow-xl"
        align="start"
      >
        <Command className="bg-transparent text-[var(--store-ink,theme(colors.charcoal.950))]">
          <CommandInput
            placeholder="Search country..."
            className="text-[var(--store-ink,theme(colors.charcoal.950))]"
          />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((c) => (
                <CommandItem
                  key={c.iso2}
                  value={`${c.name} +${c.dialCode}`}
                  onSelect={() => {
                    setCountry(c.iso2);
                    setOpen(false);
                  }}
                  className="aria-selected:bg-[var(--store-accent)]/15 aria-selected:text-[var(--store-ink,theme(colors.charcoal.950))] text-[var(--store-ink,theme(colors.charcoal.950))]"
                >
                  <span className="text-lg leading-none">{getFlagEmoji(c.iso2)}</span>
                  <span className="ml-2 flex-1 truncate text-sm">{c.name}</span>
                  <span className="ml-auto text-xs opacity-70">+{c.dialCode}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  if (variant === 'floating') {
    return (
      <div className={cn('flex items-center', className)}>
        {countryPopover}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="tel"
          value={inputValue}
          onChange={handlePhoneValueChange}
          onBlur={onBlur}
          required={required}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          placeholder={placeholder ?? '6 99 00 11 22'}
          disabled={disabled}
          className={cn(
            'flex-1 bg-transparent text-sm font-medium text-[var(--store-ink,theme(colors.charcoal.900))] outline-none placeholder:text-[var(--store-ink-muted,theme(colors.neutral.400))] placeholder:font-normal disabled:cursor-not-allowed disabled:opacity-50',
            inputClassName,
          )}
        />
      </div>
    );
  }

  return (
    <div className={cn('flex', className)}>
      {countryPopover}
      <Input
        ref={inputRef}
        id={id}
        name={name}
        type="tel"
        value={inputValue}
        onChange={handlePhoneValueChange}
        onBlur={onBlur}
        required={required}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'h-12 rounded-r-[var(--store-radius-control,theme(borderRadius.md))] rounded-l-none border border-[var(--store-border,theme(colors.slate.200))] bg-[var(--store-surface,white)] text-[var(--store-ink,theme(colors.charcoal.950))] placeholder:text-[var(--store-ink-muted,theme(colors.slate.400))] focus-visible:border-[var(--store-accent)] focus-visible:ring-2 focus-visible:ring-[var(--store-accent)]/30',
          error && 'border-rose-600 focus-visible:border-rose-600',
          inputClassName,
        )}
      />
    </div>
  );
}

export { PhoneInput };
export type { PhoneInputProps };
